import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { dfcApi } from '../api/client';

interface Props {
  empresa_id: string;
  periodo_inicio: string;
  periodo_fim: string;
}

type SemaforoCor = 'verde' | 'amarelo' | 'vermelho' | 'neutro';

function semaforo(real: number, prev: number): SemaforoCor {
  if (!prev) return 'neutro';
  const ratio = real / prev;
  if (ratio >= 1.0) return 'verde';
  if (ratio >= 0.8) return 'amarelo';
  return 'vermelho';
}

const CORES: Record<SemaforoCor, string> = {
  verde: '#48d951',
  amarelo: '#f0a500',
  vermelho: '#E24B4A',
  neutro: '#94a3b8',
};

const fmt = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

export function DFCCard({ empresa_id, periodo_inicio, periodo_fim }: Props) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['dfc', empresa_id, periodo_inicio, periodo_fim],
    queryFn: () => dfcApi.calcular(empresa_id, periodo_inicio, periodo_fim).then((r) => r.data),
    enabled: !!empresa_id,
  });

  if (isLoading) return <div style={styles.card}>Carregando...</div>;
  if (error || !data) return <div style={styles.card}>Erro ao carregar DFC.</div>;

  const consolidado = data.consolidado;
  const periodos = data.periodos;

  // Soma consolidado de todos os meses
  const somar = (linha: string, col: 'realizado' | 'previsto') =>
    periodos.reduce((acc, p) => acc + (consolidado[p]?.[linha]?.[col] ?? 0), 0);

  const receita = somar('recebimento', 'realizado');
  const receitaPrev = somar('recebimento', 'previsto');
  const mc = somar('margem_contribuicao', 'realizado');
  const mcPrev = somar('margem_contribuicao', 'previsto');
  const sf = somar('saldo_final', 'realizado');
  const sfPrev = somar('saldo_final', 'previsto');

  const metricas = [
    { label: 'Receita', valor: receita, prev: receitaPrev },
    { label: 'Margem Contribuição', valor: mc, prev: mcPrev },
    { label: 'Saldo Final', valor: sf, prev: sfPrev },
  ];

  return (
    <div style={styles.card}>
      <h3 style={styles.titulo}>
        DFC — {periodo_inicio} a {periodo_fim}
      </h3>
      <div style={styles.grid}>
        {metricas.map(({ label, valor, prev }) => {
          const cor = CORES[semaforo(valor, prev)];
          return (
            <div key={label} style={{ ...styles.metrica, borderLeft: `4px solid ${cor}` }}>
              <span style={styles.metricaLabel}>{label}</span>
              <span style={{ ...styles.metricaValor, color: cor }}>{fmt(valor)}</span>
              {prev > 0 && (
                <span style={styles.metricaPrev}>
                  orçado: {fmt(prev)} ({((valor / prev - 1) * 100).toFixed(1)}%)
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: {
    background: '#1e2130',
    borderRadius: 12,
    padding: 24,
    color: '#e2e8f0',
    fontFamily: 'Inter, system-ui, sans-serif',
    minWidth: 320,
  },
  titulo: { margin: '0 0 20px', fontSize: 16, fontWeight: 600, color: '#94a3b8' },
  grid: { display: 'flex', flexDirection: 'column', gap: 12 },
  metrica: { padding: '12px 16px', background: '#252a3d', borderRadius: 8 },
  metricaLabel: { display: 'block', fontSize: 12, color: '#94a3b8', marginBottom: 4 },
  metricaValor: { display: 'block', fontSize: 22, fontWeight: 700 },
  metricaPrev: { display: 'block', fontSize: 11, color: '#64748b', marginTop: 2 },
};
